/**
 * Inventory Module Type Definitions
 * For SLiK POS Inventory Management System
 *
 * Vision: Effortless awareness - the system thinks ahead so you don't have to.
 * Spend less time worrying about inventory and more time cutting hair.
 */

// ============================================================================
// ENUMS & TYPE UNIONS
// ============================================================================

// Product Categories - for organizing inventory by type
export type ProductCategory = 'hair' | 'styling' | 'tools' | 'supplies' | 'other';

// Vendor Types - distinguishes product suppliers from maintenance/service vendors
export type VendorType = 'product' | 'maintenance';

// Replenishment Trigger Sources - what caused item to be queued for reorder
export type ReplenishmentTrigger = 'sale' | 'manual' | 'par_alert' | 'count_variance';

// Replenishment Queue Status - lifecycle of a queue item
export type ReplenishmentStatus = 'queued' | 'in_order' | 'ordered' | 'received' | 'cancelled';

// Purchase Order Status - full workflow lifecycle
export type PurchaseOrderStatus = 'draft' | 'pending_approval' | 'approved' | 'ordered' | 'partial_received' | 'received' | 'cancelled';

// Order Method - how the order was placed
export type OrderMethod = 'manual' | 'automated';

// Transaction Types - all ways inventory can change
export type InventoryTransactionType = 'sale' | 'restock' | 'adjustment' | 'waste' | 'count_correction' | 'transfer' | 'back_bar';

// Stock Level Status - visual indicator for stock health
export type StockLevelStatus = 'ok' | 'low' | 'critical' | 'out_of_stock';

// Inventory Count Session Status
export type CountSessionStatus = 'in_progress' | 'completed' | 'cancelled';

// View Mode for product list
export type ProductViewMode = 'list' | 'grid';

// Filter options for transaction log
export type TransactionFilterType = 'all' | 'sale' | 'restock' | 'adjustment' | 'waste' | 'count_correction';

// Notification Priority
export type NotificationPriority = 'low' | 'medium' | 'high' | 'critical';

// ============================================================================
// CORE ENTITIES
// ============================================================================

/**
 * Product Interface
 * Represents an inventory product item with full tracking capabilities
 */
export interface Product {
  id: string;
  name: string;
  barcode?: string;          // UPC/EAN barcode for scanning
  sku?: string;              // Internal SKU
  brand?: string;            // Manufacturer / brand (e.g. "Clubman") — distinct from the vendor/supplier
  category: ProductCategory;
  vendorId: string;
  vendorName: string;        // Denormalized for fast display
  currentQuantity: number;
  parLevel: number;          // Low stock threshold (triggers yellow warning)
  criticalLevel: number;     // Critical threshold (triggers red warning + notification)
  reorderQuantity: number;   // Suggested order quantity when restocking
  unitCost: number;
  retailPrice?: number;      // If sold to customers
  imageUrl?: string;
  description?: string;
  isActive: boolean;
  lastCountedAt?: Date;      // For inventory count tracking
  lastOrderedAt?: Date;      // When last included in a PO
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Product Form Data (for create/update)
 */
export interface ProductFormData {
  name: string;
  barcode?: string;
  sku?: string;
  brand?: string;            // Manufacturer / brand (e.g. "Clubman")
  category: ProductCategory;
  vendorId: string;
  currentQuantity: number;
  parLevel: number;
  criticalLevel: number;
  reorderQuantity: number;
  unitCost: number;
  retailPrice?: number;
  imageUrl?: string;
  description?: string;
  isActive: boolean;
}

/**
 * Product Search/Filter Parameters
 */
export interface ProductFilterParams {
  search?: string;           // Search by name, barcode, SKU
  category?: ProductCategory;
  vendorId?: string;
  stockStatus?: StockLevelStatus;
  isActive?: boolean;
}

/**
 * Vendor Interface
 * Represents a product supplier/vendor with contact and credential info
 */
export interface Vendor {
  id: string;
  name: string;
  type: VendorType;
  contactName?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
  accountUsername?: string;  // Encrypted - for future automated ordering
  accountPassword?: string;  // Encrypted - for future automated ordering
  leadTimeDays: number;      // Expected days from order to delivery
  minimumOrder?: number;     // Minimum order amount
  productCount: number;      // Denormalized count of products from this vendor
  pendingOrdersCount: number; // Denormalized count of pending POs
  notes?: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Vendor Form Data (for create/update)
 */
export interface VendorFormData {
  name: string;
  type: VendorType;
  contactName?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
  accountUsername?: string;
  accountPassword?: string;
  leadTimeDays: number;
  minimumOrder?: number;
  notes?: string;
  isActive: boolean;
}

/**
 * Vendor with aggregated stats for display
 */
export interface VendorWithStats extends Vendor {
  totalProductValue: number;  // Sum of (unitCost * currentQuantity) for all products
  lowStockItems: number;      // Count of products below par level
}

/**
 * Replenishment Queue Item
 * Represents an item that needs to be reordered - auto-queued when below par level
 */
export interface ReplenishmentQueueItem {
  id: string;
  productId: string;
  productName: string;       // Denormalized for display
  productBarcode?: string;   // For quick identification
  vendorId: string;
  vendorName: string;        // Denormalized for grouping display
  currentQuantity: number;   // Snapshot when queued
  parLevel: number;          // Target level
  suggestedOrderQuantity: number; // Calculated based on reorderQuantity
  unitCost: number;
  estimatedCost: number;     // suggestedOrderQuantity * unitCost
  triggeredAt: Date;         // When it was added to queue
  triggeredBy: ReplenishmentTrigger;
  status: ReplenishmentStatus;
  purchaseOrderId?: string;  // Once added to a PO
  notes?: string;
}

/**
 * Replenishment Queue grouped by vendor (for UI display)
 */
export interface VendorQueueGroup {
  vendorId: string;
  vendorName: string;
  items: ReplenishmentQueueItem[];
  totalEstimatedCost: number;
  itemCount: number;
}

/**
 * Purchase Order Item
 * Individual line item in a purchase order
 */
export interface PurchaseOrderItem {
  id: string;                // Unique ID for the line item
  productId: string;
  productName: string;       // Denormalized for display
  productBarcode?: string;
  quantityOrdered: number;
  quantityReceived: number;  // Track partial receipts
  unitCost: number;
  totalCost: number;         // quantityOrdered * unitCost
  isReceived: boolean;       // Full quantity received
}

/**
 * Purchase Order
 * Full lifecycle from draft to received
 */
export interface PurchaseOrder {
  id: string;
  orderNumber: string;       // Human-readable order number (e.g., PO-2026-001)
  vendorId: string;
  vendorName: string;        // Denormalized for display
  items: PurchaseOrderItem[];
  totalCost: number;
  status: PurchaseOrderStatus;
  createdAt: Date;
  createdBy: string;         // User ID who created
  createdByName: string;     // Denormalized user name
  approvedAt?: Date;
  approvedBy?: string;       // User ID who approved
  approvedByName?: string;
  orderedAt?: Date;
  orderMethod: OrderMethod;
  expectedDeliveryDate?: Date; // Based on vendor lead time
  receivedAt?: Date;
  notes?: string;
  orderConfirmation?: string; // External order/confirmation number from vendor
}

/**
 * Purchase Order Form Data (for create/update)
 */
export interface PurchaseOrderFormData {
  vendorId: string;
  items: Array<{
    productId: string;
    quantityOrdered: number;
  }>;
  notes?: string;
}

/**
 * Inventory Transaction
 * Complete audit trail for all inventory changes
 */
export interface InventoryTransaction {
  id: string;
  productId: string;
  productName: string;       // Denormalized for display
  productBarcode?: string;
  type: InventoryTransactionType;
  quantity: number;          // @deprecated - use quantityChange
  quantityChange: number;    // Positive for increase, negative for decrease
  previousQuantity: number;
  newQuantity: number;
  referenceId?: string;      // Sale ID, PO ID, or Count Session ID
  referenceType?: 'sale' | 'purchase_order' | 'count_session' | 'manual';
  reason?: string;           // User-provided reason for adjustment
  notes?: string;
  createdBy: string;         // @deprecated - use userId
  userId: string;            // Who made the change
  userName: string;          // Denormalized for display
  barberId?: string;         // Back-bar use: WHICH barber used the product (per-barber cost)
  barberName?: string;       // Denormalized for display
  createdAt: Date;
}

/**
 * Transaction Filter Options (for transaction log view)
 */
export interface TransactionFilterOptions {
  startDate?: Date;
  endDate?: Date;
  type?: TransactionFilterType;
  productId?: string;
  userId?: string;
  limit?: number;
  offset?: number;
}

/**
 * Inventory Count Session
 * For physical inventory counting - mobile-first scanning workflow
 */
export interface InventoryCountSession {
  id: string;
  status: CountSessionStatus;
  startedAt: Date;
  startedBy: string;         // User ID
  startedByName: string;     // Denormalized
  completedAt?: Date;
  completedBy?: string;
  itemsExpected: number;     // Total products to count
  itemsCounted: number;      // Progress tracking
  items: InventoryCountItem[];
  varianceReport?: VarianceReport;
  notes?: string;
}

/**
 * Inventory Count Item
 * Individual product count within a session
 */
export interface InventoryCountItem {
  id: string;
  productId: string;
  productName: string;
  barcode?: string;
  category: ProductCategory;
  expectedQuantity: number;  // System quantity before count
  countedQuantity: number;   // What was physically counted
  variance: number;          // countedQuantity - expectedQuantity
  varianceValue: number;     // variance * unitCost
  unitCost: number;
  isCounted: boolean;        // Has this item been scanned/counted
  countedAt?: Date;
  adjustmentNote?: string;   // User note for discrepancies
}

/**
 * Variance Report
 * Summary of discrepancies from inventory count
 */
export interface VarianceReport {
  sessionId: string;
  totalItems: number;
  itemsCounted: number;
  itemsWithVariance: number;
  itemsWithPositiveVariance: number;  // Found more than expected
  itemsWithNegativeVariance: number;  // Found less than expected
  totalVarianceUnits: number;         // Sum of all variances
  totalVarianceValue: number;         // Dollar impact
  generatedAt: Date;
  generatedBy: string;
  discrepancies: VarianceDiscrepancy[];
}

/**
 * Individual variance discrepancy for report
 */
export interface VarianceDiscrepancy {
  productId: string;
  productName: string;
  barcode?: string;
  expectedQuantity: number;
  countedQuantity: number;
  variance: number;
  varianceValue: number;
  adjustmentNote?: string;
}

/**
 * Inventory Dashboard Stats
 * Real data - no placeholders
 */
export interface InventoryStats {
  totalProducts: number;
  activeProducts: number;
  lowStockCount: number;        // Products below par level
  criticalStockCount: number;   // Products below critical level
  outOfStockCount: number;      // Products at zero
  totalInventoryValue: number;  // Sum of (currentQuantity * unitCost)
  activeVendors: number;
  pendingOrdersCount: number;   // Real count from purchase_orders with status pending
  queuedItemsCount: number;     // Items in replenishment queue
}

/**
 * Product with computed stock status (for display)
 */
export interface ProductWithStatus extends Product {
  stockStatus: StockLevelStatus;
  daysUntilStockout?: number;   // Based on average daily usage (future feature)
}

/**
 * Product with Vendor Info (for display)
 * @deprecated Use ProductWithStatus instead - vendorName is now on Product
 */
export interface ProductWithVendor extends Product {
  vendorName: string;
  stockStatus: StockLevelStatus;
}

// ============================================================================
// SCANNER & QUICK ACTIONS
// ============================================================================

/**
 * Scanner Result
 * What happens when a barcode is scanned
 */
export interface ScannerResult {
  barcode: string;
  product?: Product;          // If found
  isFound: boolean;
  scannedAt: Date;
}

/**
 * Quick Adjust Data
 * For the quick adjust modal after scanning
 */
export interface QuickAdjustData {
  productId: string;
  product: Product;
  adjustmentType: 'add' | 'remove' | 'set';
  quantity: number;
  reason?: string;
}

// ============================================================================
// NOTIFICATIONS
// ============================================================================

/**
 * Inventory Notification
 * Push notification for stock alerts
 */
export interface InventoryNotification {
  id: string;
  type: 'low_stock' | 'critical_stock' | 'out_of_stock' | 'order_received' | 'count_due';
  priority: NotificationPriority;
  title: string;
  message: string;
  productId?: string;
  productName?: string;
  purchaseOrderId?: string;
  isRead: boolean;
  createdAt: Date;
  readAt?: Date;
}

// ============================================================================
// EXPORT & REPORTING
// ============================================================================

/**
 * Transaction Export Data (for CSV export)
 */
export interface TransactionExportRow {
  date: string;
  productName: string;
  barcode: string;
  transactionType: string;
  quantityChange: number;
  previousQuantity: number;
  newQuantity: number;
  userName: string;
  reason: string;
  notes: string;
}

/**
 * Inventory Report Data
 */
export interface InventoryReportData {
  generatedAt: Date;
  stats: InventoryStats;
  lowStockProducts: ProductWithStatus[];
  criticalStockProducts: ProductWithStatus[];
  recentTransactions: InventoryTransaction[];
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Determine stock level status based on quantity thresholds
 */
export function getStockLevelStatus(
  currentQuantity: number,
  parLevel: number,
  criticalLevel: number
): StockLevelStatus {
  if (currentQuantity === 0) return 'out_of_stock';
  if (currentQuantity <= criticalLevel) return 'critical';
  if (currentQuantity <= parLevel) return 'low';
  return 'ok';
}

/**
 * Calculate suggested order quantity
 * Orders enough to reach par level plus buffer
 */
export function calculateSuggestedOrderQuantity(
  currentQuantity: number,
  parLevel: number,
  reorderQuantity: number
): number {
  const deficit = parLevel - currentQuantity;
  // Order at least the reorder quantity, or enough to reach par + 20% buffer
  return Math.max(reorderQuantity, Math.ceil(deficit * 1.2));
}

/**
 * Generate a human-readable order number
 */
export function generateOrderNumber(existingCount: number): string {
  const year = new Date().getFullYear();
  const paddedNumber = String(existingCount + 1).padStart(3, '0');
  return `PO-${year}-${paddedNumber}`;
}

// ============================================================================
// DISPLAY LABELS & CONSTANTS
// ============================================================================

/**
 * Category Display Labels
 */
export const CATEGORY_LABELS: Record<ProductCategory, string> = {
  hair: 'Hair Products',
  styling: 'Styling Products',
  tools: 'Tools & Equipment',
  supplies: 'Shop Supplies',
  other: 'Other'
};

/**
 * Vendor Type Display Labels
 */
export const VENDOR_TYPE_LABELS: Record<VendorType, string> = {
  product: 'Product Vendor',
  maintenance: 'Maintenance/Supplies'
};

/**
 * Transaction Type Display Labels
 */
export const TRANSACTION_TYPE_LABELS: Record<InventoryTransactionType, string> = {
  sale: 'Sale',
  restock: 'Restock',
  adjustment: 'Manual Adjustment',
  waste: 'Waste/Damage',
  count_correction: 'Count Correction',
  transfer: 'Transfer',
  back_bar: 'Back-bar use'
};

/**
 * Purchase Order Status Labels
 */
export const PO_STATUS_LABELS: Record<PurchaseOrderStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  ordered: 'Ordered',
  partial_received: 'Partially Received',
  received: 'Received',
  cancelled: 'Cancelled'
};

/**
 * Stock Level Status Labels and Colors
 */
export const STOCK_STATUS_CONFIG: Record<StockLevelStatus, { label: string; color: string; bgColor: string }> = {
  ok: { label: 'In Stock', color: 'text-green-600', bgColor: 'bg-green-100' },
  low: { label: 'Low Stock', color: 'text-yellow-600', bgColor: 'bg-yellow-100' },
  critical: { label: 'Critical', color: 'text-red-600', bgColor: 'bg-red-100' },
  out_of_stock: { label: 'Out of Stock', color: 'text-red-800', bgColor: 'bg-red-200' }
};

/**
 * Replenishment Status Labels
 */
export const REPLENISHMENT_STATUS_LABELS: Record<ReplenishmentStatus, string> = {
  queued: 'Queued',
  in_order: 'In Order',
  ordered: 'Ordered',
  received: 'Received',
  cancelled: 'Cancelled'
};

/**
 * Count Session Status Labels
 */
export const COUNT_SESSION_STATUS_LABELS: Record<CountSessionStatus, string> = {
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled'
};

// ============================================================================
// FIREBASE COLLECTION NAMES
// ============================================================================

export const INVENTORY_COLLECTIONS = {
  PRODUCTS: 'inventory_products',
  VENDORS: 'inventory_vendors',
  QUEUE: 'inventory_queue',
  PURCHASE_ORDERS: 'inventory_purchase_orders',
  TRANSACTIONS: 'inventory_transactions',
  COUNT_SESSIONS: 'inventory_count_sessions',
  NOTIFICATIONS: 'inventory_notifications'
} as const;
